import React, { useCallback, useEffect, useState } from "react";
import {
  MdSettings,
  MdNotifications,
  MdSchedule,
  MdWarning,
  MdSave,
  MdAdd,
  MdDelete,
  MdContentCopy,
  MdCheck,
  MdLink,
  MdQuestionAnswer,
  MdEmail,
  MdDescription,
} from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Button, { IconButton } from "components/button";
import Badge from "components/badge";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { InputField, Select, Checkbox, Switch } from "components/fields";
import Tabs, { TabPanel } from "components/tabs";
import Loader from "components/loader/Loader";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { getAudience } from "components/assessment/audiences";
import PulseQuestionsSettings from "components/assessment/PulseQuestionsSettings";
import notify from "lib/notify";
import { statusTone } from "lib/statusTone";
import { getApiErrorMessage } from "api";

const TABS_ID = "assessment-settings-tabs";

const TAB_DEFS = [
  { id: "pulse", label: "Check-In Settings", icon: MdSchedule },
  { id: "questions", label: "Questions", icon: MdQuestionAnswer },
  { id: "notifications", label: "Notifications", icon: MdNotifications },
  { id: "thresholds", label: "Thresholds", icon: MdWarning },
  { id: "intake", label: "Intake Forms", icon: MdSettings },
];

const TABS = TAB_DEFS.map(({ id, label, icon: Icon }) => ({
  id,
  label: (
    <>
      <Icon aria-hidden="true" className="h-4 w-4" />
      {label}
    </>
  ),
}));

const WEEKDAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
];

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
];

const DELIVERY_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Email + SMS" },
];

const TARGET_AUDIENCE_OPTIONS = [
  { value: "both", label: "Everyone (Staff + Volunteers)" },
  { value: "staff", label: "Staff Only" },
  { value: "volunteer", label: "Volunteers Only" },
];

const DAY_TO_NUMBER = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5 };
const NUMBER_TO_DAY = { 1: "monday", 2: "tuesday", 3: "wednesday", 4: "thursday", 5: "friday" };

const NOTIFICATION_OPTIONS = [
  {
    key: "careTaskCreated",
    label: "Care Task Created",
    description: "Notify when a new care task requires attention",
  },
  {
    key: "burnoutRiskHigh",
    label: "High Burnout Risk",
    description: "Notify when someone is flagged as high burnout risk",
  },
  {
    key: "lowResponseRate",
    label: "Low Response Rate",
    description: "Notify when check-in survey response rate drops below 50%",
  },
  {
    key: "newIntakeSubmission",
    label: "New Intake Submission",
    description: "Notify when someone completes an intake form",
  },
];

const THRESHOLD_FIELDS = [
  {
    key: "burnoutHighThreshold",
    label: "High Burnout Risk Threshold (%)",
    hint: "Score above this is flagged as high risk",
    min: 0,
    max: 100,
  },
  {
    key: "burnoutModerateThreshold",
    label: "Moderate Burnout Risk Threshold (%)",
    hint: "Score between this and high threshold is moderate",
    min: 0,
    max: 100,
  },
  {
    key: "roleFitLowThreshold",
    label: "Low Role Fit Threshold (%)",
    hint: "Role fit below this triggers alerts",
    min: 0,
    max: 100,
  },
  {
    key: "careTaskSlaHours",
    label: "Care Task SLA (Hours)",
    hint: "Expected time to address care tasks",
    min: 1,
    max: 168,
  },
];

const DEFAULT_SURVEY_QUESTIONS = [
  { id: 1, type: "text", question: "Tell us a story you heard on Sunday or Wednesday from the congregation" },
  { id: 2, type: "text", question: "What was the main thing you worked on this week?" },
  { id: 3, type: "text", question: "Is there anything blocking you right now?" },
  { id: 4, type: "rating", question: "How well do you feel about your role here?" },
  { id: 5, type: "rating", question: "How well do you feel you're contributing to the church's goals and mission?" },
];

const CREATE_FORM_ID = "create-intake-form";

export default function Settings({ audience }) {
  const config = getAudience(audience);
  const { service, labels, intakePath } = config;
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("pulse");
  const [intakeForms, setIntakeForms] = useState([]);
  const [funQuestions, setFunQuestions] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const [showDigestDialog, setShowDigestDialog] = useState(false);
  const [digestRecipients, setDigestRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientsError, setRecipientsError] = useState(null);

  const defaultFormName = `${labels.adjective} Personality Assessment`;
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newFormName, setNewFormName] = useState(defaultFormName);
  const [creatingForm, setCreatingForm] = useState(false);

  const [pulseSettings, setPulseSettings] = useState({
    frequency: "weekly",
    dayOfWeek: "monday",
    timeOfDay: "09:00",
    deliveryMethod: "email",
    targetAudience: "both",
    reminderEnabled: true,
    reminderDays: 2,
  });

  const [digestSettings, setDigestSettings] = useState({
    digestEnabled: false,
    digestDay: "friday",
    digestTime: "17:00",
  });

  const [notificationSettings, setNotificationSettings] = useState({
    careTaskCreated: true,
    burnoutRiskHigh: true,
    lowResponseRate: true,
    newIntakeSubmission: true,
    notifyEmails: "",
  });

  const [thresholdSettings, setThresholdSettings] = useState({
    burnoutHighThreshold: 70,
    burnoutModerateThreshold: 40,
    roleFitLowThreshold: 50,
    careTaskSlaHours: 48,
  });

  // The first load drives the page skeleton/error state; refreshes after a
  // mutation keep the page in place and report failures as a toast.
  const fetchSettings = useCallback(
    async ({ initial = false } = {}) => {
      if (initial) {
        setLoading(true);
        setLoadError(null);
      }
      try {
        const [formsRes, questionsRes, surveysRes] = await Promise.all([
          service.getIntakeForms(),
          service.getFunQuestions(),
          service.getSurveys(),
        ]);

        if (formsRes.success) setIntakeForms(formsRes.data || []);
        if (questionsRes.success) setFunQuestions(questionsRes.data || []);

        if (surveysRes.success && surveysRes.data?.length > 0) {
          const survey = surveysRes.data[0];
          setPulseSettings((prev) => ({
            ...prev,
            frequency: survey.frequency || prev.frequency,
            dayOfWeek: NUMBER_TO_DAY[survey.sendDay] || prev.dayOfWeek,
            timeOfDay: survey.sendTime ? survey.sendTime.slice(0, 5) : prev.timeOfDay,
            deliveryMethod: survey.deliveryMethod || prev.deliveryMethod,
            targetAudience: survey.targetAudience || prev.targetAudience,
          }));
          if (survey.digestEnabled !== undefined) {
            setDigestSettings({
              digestEnabled: survey.digestEnabled || false,
              digestDay: NUMBER_TO_DAY[survey.digestDay] || "friday",
              digestTime: survey.digestTime ? survey.digestTime.slice(0, 5) : "17:00",
            });
          }
        }
      } catch (err) {
        if (initial) {
          setLoadError(err);
        } else {
          notify.error(getApiErrorMessage(err, "Could not refresh settings"));
        }
      } finally {
        if (initial) setLoading(false);
      }
    },
    [service]
  );

  useEffect(() => {
    fetchSettings({ initial: true });
  }, [fetchSettings]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const surveyPayload = {
        name: "Weekly Check-In",
        surveyType: "wellbeing",
        frequency: pulseSettings.frequency,
        sendDay: DAY_TO_NUMBER[pulseSettings.dayOfWeek] || 1,
        sendTime: pulseSettings.timeOfDay + ":00",
        deliveryMethod: pulseSettings.deliveryMethod,
        targetAudience: pulseSettings.targetAudience,
        isActive: true,
        digestEnabled: digestSettings.digestEnabled,
        digestDay: DAY_TO_NUMBER[digestSettings.digestDay] || 5,
        digestTime: digestSettings.digestTime + ":00",
        questions: DEFAULT_SURVEY_QUESTIONS,
      };

      // Upsert: update the organization's survey when it exists, otherwise create it.
      const surveysRes = await service.getSurveys();
      if (surveysRes.success && surveysRes.data?.length > 0) {
        await service.updateSurvey(surveysRes.data[0].id, surveyPayload);
      } else {
        await service.createSurvey(surveyPayload);
      }

      notify.success("Settings saved");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  const loadDigestRecipients = async () => {
    setLoadingRecipients(true);
    setRecipientsError(null);
    try {
      const res = await service.getDigestRecipients();
      if (res.success) {
        setDigestRecipients(res.data || []);
        setSelectedRecipients((res.data || []).map((a) => a.id));
      }
    } catch (err) {
      setRecipientsError(err);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleOpenDigestDialog = () => {
    setShowDigestDialog(true);
    loadDigestRecipients();
  };

  const closeDigestDialog = () => {
    if (sendingDigest) return;
    setShowDigestDialog(false);
  };

  const handleSendDigest = async () => {
    if (selectedRecipients.length === 0) {
      notify.error("Select at least one recipient");
      return;
    }
    setSendingDigest(true);
    try {
      const res = await service.sendDigestNow(selectedRecipients);
      if (res.success) {
        notify.success(res.data?.message || "Digest sent");
        setShowDigestDialog(false);
      }
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not send digest"));
    } finally {
      setSendingDigest(false);
    }
  };

  const toggleRecipient = (id) => {
    setSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const toggleAllRecipients = () => {
    if (selectedRecipients.length === digestRecipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(digestRecipients.map((a) => a.id));
    }
  };

  const getPublicIntakeUrl = (form) => {
    if (!form.slug) return null;
    return `${window.location.origin}${intakePath}/${form.slug}`;
  };

  const handleCopyLink = async (form) => {
    const url = getPublicIntakeUrl(form);
    if (!url) {
      notify.error("Could not generate link");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(form.id);
      notify.success("Link copied");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      notify.error("Could not copy link");
    }
  };

  const openCreateDialog = () => {
    setNewFormName(defaultFormName);
    setShowCreateDialog(true);
  };

  const closeCreateDialog = () => {
    if (creatingForm) return;
    setShowCreateDialog(false);
  };

  const handleCreateIntakeForm = async (event) => {
    event.preventDefault();
    const name = newFormName.trim();
    if (!name) return;
    setCreatingForm(true);
    try {
      await service.createIntakeForm({
        name,
        isActive: true,
        targetAudience: "both",
      });
      notify.success("Intake form created");
      setShowCreateDialog(false);
      fetchSettings();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not create intake form"));
    } finally {
      setCreatingForm(false);
    }
  };

  const handleDeleteIntakeForm = async (form) => {
    const confirmed = await confirm({
      title: `Delete "${form.name}"?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await service.deleteIntakeForm(form.id);
      notify.success("Intake form deleted");
      fetchSettings();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete intake form"));
    }
  };

  const ready = !loading && !loadError;

  const header = (
    <PageHeader
      title="Settings"
      description={`Configure ${labels.adjective.toLowerCase()} module preferences and thresholds`}
      actions={
        ready && (
          <Button leftIcon={<MdSave />} loading={saving} onClick={handleSaveSettings}>
            Save Settings
          </Button>
        )
      }
      tabs={
        ready && (
          <Tabs
            id={TABS_ID}
            aria-label="Settings sections"
            syncParam="tab"
            tabs={TABS}
            value={activeTab}
            onChange={setActiveTab}
          />
        )
      }
    />
  );

  if (loading) {
    return (
      <PageContainer>
        {header}
        <Skeleton className="h-10 w-full max-w-2xl rounded-control" />
        <Skeleton className="h-96 w-full rounded-card" />
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        {header}
        <ErrorState
          message={getApiErrorMessage(loadError, "Could not load settings")}
          onRetry={() => fetchSettings({ initial: true })}
        />
      </PageContainer>
    );
  }

  const allRecipientsSelected =
    digestRecipients.length > 0 && selectedRecipients.length === digestRecipients.length;

  return (
    <PageContainer>
      {header}

      <TabPanel tabsId={TABS_ID} tabId={activeTab} className="flex flex-col gap-5">
        {activeTab === "pulse" && (
          <>
            <Card padding="md">
              <CardHeader title="Check-In Survey Settings" className="mb-6" />
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Select
                  label="Survey Frequency"
                  value={pulseSettings.frequency}
                  onChange={(e) => setPulseSettings({ ...pulseSettings, frequency: e.target.value })}
                  options={FREQUENCY_OPTIONS}
                />
                <Select
                  label="Day of Week"
                  value={pulseSettings.dayOfWeek}
                  onChange={(e) => setPulseSettings({ ...pulseSettings, dayOfWeek: e.target.value })}
                  options={WEEKDAY_OPTIONS}
                />
                <InputField
                  label="Time of Day"
                  type="time"
                  value={pulseSettings.timeOfDay}
                  onChange={(e) => setPulseSettings({ ...pulseSettings, timeOfDay: e.target.value })}
                />
                <Select
                  label="Delivery Method"
                  value={pulseSettings.deliveryMethod}
                  onChange={(e) =>
                    setPulseSettings({ ...pulseSettings, deliveryMethod: e.target.value })
                  }
                  options={DELIVERY_OPTIONS}
                />
                <Select
                  label="Target Audience"
                  hint="Who receives the weekly check-in survey"
                  value={pulseSettings.targetAudience}
                  onChange={(e) =>
                    setPulseSettings({ ...pulseSettings, targetAudience: e.target.value })
                  }
                  options={TARGET_AUDIENCE_OPTIONS}
                />
                <div className="flex flex-col gap-3 md:col-span-2">
                  <Checkbox
                    label="Send reminder if no response"
                    checked={pulseSettings.reminderEnabled}
                    onChange={(e) =>
                      setPulseSettings({ ...pulseSettings, reminderEnabled: e.target.checked })
                    }
                  />
                  {pulseSettings.reminderEnabled && (
                    <InputField
                      label="Remind After (Days)"
                      type="number"
                      min="1"
                      max="7"
                      className="w-40"
                      value={pulseSettings.reminderDays}
                      onChange={(e) =>
                        setPulseSettings({
                          ...pulseSettings,
                          reminderDays: parseInt(e.target.value, 10),
                        })
                      }
                    />
                  )}
                </div>
              </div>
            </Card>

            <Card padding="md">
              <CardHeader
                title="Weekly Digest Email"
                description={`Send a weekly summary email to admins showing ${labels.adjective.toLowerCase()} check-in health scores, sorted by those needing attention first.`}
                className="mb-6"
              />
              <Checkbox
                label="Enable weekly digest email"
                checked={digestSettings.digestEnabled}
                onChange={(e) =>
                  setDigestSettings({ ...digestSettings, digestEnabled: e.target.checked })
                }
              />

              {digestSettings.digestEnabled && (
                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                  <Select
                    label="Send Day"
                    value={digestSettings.digestDay}
                    onChange={(e) => setDigestSettings({ ...digestSettings, digestDay: e.target.value })}
                    options={WEEKDAY_OPTIONS}
                  />
                  <InputField
                    label="Send Time"
                    type="time"
                    value={digestSettings.digestTime}
                    onChange={(e) => setDigestSettings({ ...digestSettings, digestTime: e.target.value })}
                  />
                </div>
              )}

              <div className="mt-6 border-t border-line pt-6">
                <Button variant="secondary" leftIcon={<MdEmail />} onClick={handleOpenDigestDialog}>
                  Send Digest Now
                </Button>
                <p className="mt-2 text-xs text-fg-muted">
                  Manually send this week's digest to selected admin users
                </p>
              </div>
            </Card>
          </>
        )}

        {activeTab === "questions" && <PulseQuestionsSettings config={config} />}

        {activeTab === "notifications" && (
          <Card padding="md">
            <CardHeader title="Notification Settings" className="mb-6" />
            <div className="flex flex-col gap-5">
              {NOTIFICATION_OPTIONS.map((option) => (
                <Switch
                  key={option.key}
                  label={option.label}
                  description={option.description}
                  checked={notificationSettings[option.key]}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      [option.key]: e.target.checked,
                    })
                  }
                />
              ))}
            </div>
            <div className="mt-6 border-t border-line pt-6">
              <InputField
                label="Notification Recipients"
                hint="Separate multiple email addresses with commas"
                value={notificationSettings.notifyEmails}
                onChange={(e) =>
                  setNotificationSettings({ ...notificationSettings, notifyEmails: e.target.value })
                }
                placeholder="admin@church.org, pastor@church.org"
              />
            </div>
          </Card>
        )}

        {activeTab === "thresholds" && (
          <Card padding="md">
            <CardHeader title="Alert Thresholds" className="mb-6" />
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {THRESHOLD_FIELDS.map((field) => (
                <InputField
                  key={field.key}
                  label={field.label}
                  hint={field.hint}
                  type="number"
                  min={field.min}
                  max={field.max}
                  value={thresholdSettings[field.key]}
                  onChange={(e) =>
                    setThresholdSettings({
                      ...thresholdSettings,
                      [field.key]: parseInt(e.target.value, 10),
                    })
                  }
                />
              ))}
            </div>
          </Card>
        )}

        {activeTab === "intake" && (
          <>
            <Card padding="md">
              <CardHeader
                title="Intake Forms"
                className="mb-6"
                actions={
                  intakeForms.length > 0 && (
                    <Button leftIcon={<MdAdd />} onClick={openCreateDialog}>
                      Create Form
                    </Button>
                  )
                }
              />

              {intakeForms.length === 0 ? (
                <EmptyState
                  icon={MdDescription}
                  title="No Intake Forms Yet"
                  description={`Create a form to share a public personality assessment link with your ${labels.pluralLower}.`}
                  action={
                    <Button leftIcon={<MdAdd />} onClick={openCreateDialog}>
                      Create Form
                    </Button>
                  }
                />
              ) : (
                <ul className="divide-y divide-line">
                  {intakeForms.map((form) => {
                    const publicUrl = getPublicIntakeUrl(form);
                    const status = form.isActive ? "Active" : "Inactive";
                    return (
                      <li key={form.id} className="py-4 first:pt-0 last:pb-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-fg">{form.name}</p>
                              <Badge tone={statusTone(status)}>{status}</Badge>
                            </div>
                            <p className="mt-1 text-sm text-fg-muted">
                              {form.formQuestions?.length || form.questionCount || 0} questions •{" "}
                              {form.submissionCount || 0} submissions
                            </p>
                          </div>
                          <IconButton
                            icon={<MdDelete />}
                            label={`Delete ${form.name}`}
                            className="text-danger-fg"
                            onClick={() => handleDeleteIntakeForm(form)}
                          />
                        </div>

                        {publicUrl && (
                          <div className="mt-3 flex items-center gap-2 rounded-control bg-surface-subtle p-3">
                            <MdLink aria-hidden="true" className="h-4 w-4 shrink-0 text-fg-muted" />
                            <code className="min-w-0 flex-1 truncate text-xs text-fg-muted">
                              {publicUrl}
                            </code>
                            <Button
                              size="sm"
                              variant="secondary"
                              leftIcon={copiedId === form.id ? <MdCheck /> : <MdContentCopy />}
                              onClick={() => handleCopyLink(form)}
                            >
                              {copiedId === form.id ? "Copied" : "Copy Link"}
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card padding="md">
              <CardHeader title="Fun Questions Library" className="mb-6" />
              {funQuestions.length === 0 ? (
                <EmptyState variant="inline" title="No fun questions available" />
              ) : (
                <ul className="flex flex-col gap-2">
                  {funQuestions.map((question) => (
                    <li
                      key={question.id}
                      className="flex items-center justify-between gap-3 rounded-control bg-surface-subtle p-3"
                    >
                      <p className="text-sm text-fg">{question.questionText}</p>
                      <Badge tone="neutral">{question.category || "General"}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </TabPanel>

      <Dialog
        isOpen={showDigestDialog}
        onClose={closeDigestDialog}
        title={`Send ${labels.adjective} Digest`}
        description="Select which admins should receive this week's digest email."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={closeDigestDialog} disabled={sendingDigest}>
              Cancel
            </Button>
            <Button
              leftIcon={<MdEmail />}
              loading={sendingDigest}
              disabled={loadingRecipients || selectedRecipients.length === 0}
              onClick={handleSendDigest}
            >
              {`Send ${labels.adjective} Digest`}
            </Button>
          </>
        }
      >
        {loadingRecipients ? (
          <Loader size="md" label="Loading recipients" />
        ) : recipientsError ? (
          <ErrorState
            message={getApiErrorMessage(recipientsError, "Could not load recipients")}
            onRetry={loadDigestRecipients}
          />
        ) : digestRecipients.length === 0 ? (
          <EmptyState variant="inline" title="No admin users found" />
        ) : (
          <div className="flex flex-col gap-3">
            <Checkbox
              label={`Select All (${digestRecipients.length})`}
              checked={allRecipientsSelected}
              onChange={toggleAllRecipients}
            />
            <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto border-t border-line pt-3">
              {digestRecipients.map((admin) => (
                <li key={admin.id} className="rounded-control p-2 hover:bg-surface-subtle">
                  <Checkbox
                    label={`${admin.firstName} ${admin.lastName}`}
                    description={admin.email}
                    checked={selectedRecipients.includes(admin.id)}
                    onChange={() => toggleRecipient(admin.id)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={showCreateDialog}
        onClose={closeCreateDialog}
        title="Create Intake Form"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={closeCreateDialog} disabled={creatingForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              form={CREATE_FORM_ID}
              loading={creatingForm}
              disabled={!newFormName.trim()}
            >
              Create Form
            </Button>
          </>
        }
      >
        <form id={CREATE_FORM_ID} onSubmit={handleCreateIntakeForm}>
          <InputField
            label="Form Name"
            required
            value={newFormName}
            onChange={(e) => setNewFormName(e.target.value)}
          />
        </form>
      </Dialog>
    </PageContainer>
  );
}
